import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { algoliaClient } from '../../../api/algolia/algolia.client';
import { ALG_API_KEY, ALG_APP_ID } from '../../../constants/algolia';
import { MEDIA_BREAKPOINTS } from '../../../constants/MEDIA_BREAKPOINTS';
import { listOfAllFacetKeys, nbItemsPerPage, searchableAttributes, sortConfigByNameOrIndex, useAnalytic, showFilterByTypes, fullListOfItemsForFacets, defaultSearchParams } from '../../configs';
import { createFiltersString } from '../../utils/createFiltersString';
import { sortFacetValues } from '../../utils/sortFacetValues';
import { FACET_KEY_TYPES } from '../../constants/facetKeys';
import { SEARCH_PARAM_KEY_PAGE_NUMBER, SEARCH_PARAM_KEY_SORT, SEARCH_PARAM_KEY_TYPE } from '../../constants/searchParamKeys';
import { convertSymbolsToEncodedUnicode } from '../../../utils/convertSymbolsToEncodedUnicode';
import { useDynamicConfigs } from '../../hooks/useDynamicConfigs';
const appWrapper = document.querySelector('.alg-app');
const CLIENT = algoliaClient(ALG_APP_ID, ALG_API_KEY);
const queryParams = {
    analytics: useAnalytic,
    clickAnalytics: useAnalytic,
    hitsPerPage: nbItemsPerPage + 1,
    page: 0,
    facets: listOfAllFacetKeys,
    ...(searchableAttributes !== undefined && { restrictSearchableAttributes: searchableAttributes }),
    filters: ''
};
let activeIndex;
let searchIndex;
const getInitialDataState = () => ({
    loading: true
});
const getInitialData = () => ({
    hits: [],
    facets: {},
    hierarchyFacets: {},
    facetsFromServer: {},
    nbHits: 0,
    nbPages: 0,
    queryID: ''
});
const transformFacetsData = (facetsFromServer, sortingOrders) => {
    const facets = {};
    if (showFilterByTypes && facetsFromServer[FACET_KEY_TYPES] !== undefined) {
        facets[FACET_KEY_TYPES] = Object.entries(facetsFromServer[FACET_KEY_TYPES]).map(([name, count]) => {
            return {
                label: name,
                value: name.toLowerCase(),
                count
            };
        });
    }
    if (sortingOrders?.types !== undefined && facets[FACET_KEY_TYPES] !== undefined) {
        facets[FACET_KEY_TYPES] = sortFacetValues(facets[FACET_KEY_TYPES], sortingOrders.types.order);
    }
    return { facets };
};
const responseHandler = (response) => {
    const data = getInitialData();
    const facetsFromServer = response.facets;
    data.hits = response.hits;
    data.nbHits = response.nbSortedHits ?? response.nbHits;
    data.nbPages = response.nbPages;
    data.queryID = response.queryID;
    fullListOfItemsForFacets.forEach(([key, values]) => {
        facetsFromServer[key] = { ...values, ...facetsFromServer[key] };
    });
    data.facetsFromServer = facetsFromServer;
    const { facets } = transformFacetsData(facetsFromServer, response.renderingContent?.facetOrdering?.values);
    data.facets = facets;
    return data;
};
export const useAlgoliaCapsuleSearch = () => {
    const { defaultFilters } = useDynamicConfigs();
    const { collection } = useDynamicConfigs();
    const { handle } = useParams();
    const [dataState, setDataState] = useState(getInitialDataState);
    const [data, setData] = useState(getInitialData);
    const [searchParams] = useSearchParams();
    const capsuleItemsArr = collection?.capsuleItems ?? [];
    useLayoutEffect(() => {
        setDataState({ loading: true });
    }, [searchParams, handle]);
    useEffect(() => {
        let canceled = false;
        const filters = createFiltersString(defaultFilters);
        const query = '';
        queryParams.page = +(searchParams.get(SEARCH_PARAM_KEY_PAGE_NUMBER) ?? 1) - 1;
        const sortConfig = sortConfigByNameOrIndex[searchParams.get(SEARCH_PARAM_KEY_SORT) ?? defaultSearchParams.index];
        const sortIndex = sortConfig?.index ?? defaultSearchParams.index;
        if (sortIndex !== activeIndex) {
            searchIndex = CLIENT.initIndex(sortIndex);
            activeIndex = sortIndex;
        }
        if (sortConfig?.filters !== undefined) {
            filters.add(sortConfig.filters);
        }
        if (showFilterByTypes) {
            const type = searchParams.get(SEARCH_PARAM_KEY_TYPE);
            if (type !== null) {
                filters.add(`${FACET_KEY_TYPES}:"${type}"`);
            }
        }
        if (capsuleItemsArr.length > 0) {
            const capsuleFilters = createFiltersString('');
            capsuleItemsArr.forEach(sku => {
                capsuleFilters.add(`objectID:${sku}`, 'OR');
            });
            filters.add(`(${capsuleFilters.value()})`, 'AND');
        }
        queryParams.filters = filters.value();
        const wrapperRectTop = appWrapper.getBoundingClientRect().top;
        if (wrapperRectTop < 0) {
            window.scrollTo({
                top: wrapperRectTop + window.scrollY + (MEDIA_BREAKPOINTS.XL_UP.matches ? 250 : 195),
                behavior: 'smooth'
            });
        }
        void (async () => {
            const response = await searchIndex.search(convertSymbolsToEncodedUnicode(query), queryParams);
            if (!canceled) {
                setData(responseHandler(response));
                setDataState({ loading: false });
            }
        })();
        return () => { canceled = true; };
    }, [searchParams, handle]);
    return [data, dataState];
};
